import React, { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";

const BARS = 36;
const cache = new Map();

async function computePeaks(url) {
  if (cache.has(url)) return cache.get(url);
  const res = await fetch(url);
  const buf = await res.arrayBuffer();
  const Ctx = window.AudioContext || window.webkitAudioContext;
  const ctx = new Ctx();
  const audio = await ctx.decodeAudioData(buf);
  const data = audio.getChannelData(0);
  const step = Math.max(1, Math.floor(data.length / BARS));
  const peaks = [];
  for (let i = 0; i < BARS; i++) {
    let max = 0;
    for (let j = i * step; j < (i + 1) * step && j < data.length; j += 8) max = Math.max(max, Math.abs(data[j]));
    peaks.push(max);
  }
  const norm = Math.max(...peaks, 0.01);
  const out = peaks.map(p => Math.max(0.12, p / norm));
  ctx.close?.();
  cache.set(url, out);
  return out;
}

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
const SPEEDS = [1, 1.5, 2];

export default function AudioMessage({ src, duration, mine, testid }) {
  const audioRef = useRef(null);
  const [peaks, setPeaks] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(duration || 0);
  const [speed, setSpeed] = useState(1);

  useEffect(() => { let ok = true; computePeaks(src).then(p => ok && setPeaks(p)).catch(() => ok && setPeaks(Array(BARS).fill(0.4))); return () => { ok = false; }; }, [src]);
  useEffect(() => { if (audioRef.current) audioRef.current.playbackRate = speed; }, [speed]);

  const toggle = () => { const a = audioRef.current; if (!a) return; if (a.paused) { a.playbackRate = speed; a.play(); } else a.pause(); };
  const seek = (e) => {
    const a = audioRef.current; if (!a || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    a.currentTime = Math.min(dur, Math.max(0, ((e.clientX - rect.left) / rect.width) * dur));
  };
  const cycleSpeed = () => setSpeed(s => SPEEDS[(SPEEDS.indexOf(s) + 1) % SPEEDS.length]);
  const progress = dur ? t / dur : 0;
  const played = mine ? "bg-white" : "bg-cyan-500";
  const rest = mine ? "bg-white/40" : "bg-cyan-500/30";

  return (
    <div className="flex items-center gap-2 min-w-[220px] max-w-[280px] py-1" data-testid={testid}>
      <audio ref={audioRef} src={src} preload="metadata"
        onLoadedMetadata={(e) => { if (isFinite(e.target.duration)) setDur(e.target.duration); }}
        onTimeUpdate={(e) => setT(e.target.currentTime)} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => { setPlaying(false); setT(0); }} />
      <button type="button" onClick={toggle} data-testid={`${testid}-play`} aria-label={playing ? "Pausar" : "Reproduzir"}
        className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${mine ? "bg-white/20 hover:bg-white/30" : "bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-600"}`}>
        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-end gap-[2px] h-7 cursor-pointer" onClick={seek} data-testid={`${testid}-wave`} role="slider" aria-valuenow={Math.round(progress * 100)}>
          {(peaks || Array(BARS).fill(0.3)).map((p, i) => (
            <span key={i} className={`flex-1 rounded-full transition-colors ${i / BARS <= progress ? played : rest} ${!peaks ? "animate-pulse" : ""}`} style={{ height: `${Math.round(p * 100)}%` }} />
          ))}
        </div>
        <div className={`flex items-center justify-between text-[10px] mt-0.5 ${mine ? "text-white/80" : "text-muted-foreground"}`}>
          <span>{fmt(playing || t > 0 ? t : dur)}</span>
          <button type="button" onClick={cycleSpeed} data-testid={`${testid}-speed`} className={`px-1.5 py-0.5 rounded-full font-semibold ${mine ? "bg-white/20" : "bg-muted"}`}>{speed}x</button>
        </div>
      </div>
    </div>
  );
}
